import { trackAgentRunStarted, trackAgentRunCompleted } from '~/telemetry/usePostHog';

/**
 * Reports `agent-run-started` / `agent-run-completed` for the assist agent by
 * watching its SSE stream. One wrapper around the stream, so no component or
 * composable has to know about analytics.
 *
 * A run is one POST /v0/assist/{thread}/stream (a question) or
 * /commands/goto (a jump, e.g. an image remix). /commands/resume continues a run
 * that paused for the user (an `interrupts` event), so it is not a new run.
 * A run that ends paused is not complete: it is reported once its resume ends.
 */

const ASSIST_URL = /\/v0\/assist\/([^/]+)\/(stream|commands\/goto|commands\/resume)$/;

// Only the new agent streams over this route, and it only serves Assist.
const CONVERSATION_TYPE = 'assist';

// The filter keys a run can carry, and the name each is reported under. Anything
// else is dropped rather than reported: `setFiltersObject` can put arbitrary keys
// in the store, and only these four are known to be fixed field names.
const FILTER_NAMES = {
  'metadata.product': 'product',
  'metadata.country': 'country',
  'metadata.status': 'status',
  'metadata.externalId': 'collection',
};

/**
 * Which kinds of filter a run carried: the names only, sorted and comma-joined,
 * or `none`. The values (products, countries, document ids) never leave.
 *
 * @param {Array<{key: string, value: unknown}>} [filters] - The request's `filters`.
 */
export const describeFilters = (filters) => {
  const names = new Set();
  (Array.isArray(filters) ? filters : []).forEach((filter) => {
    const name = FILTER_NAMES[filter?.key];
    if (name) names.add(name);
  });
  return names.size ? [...names].sort().join(',') : 'none';
};

// Thread id -> the run in flight. Keyed by id so a resume finds its run. The
// thread id is the conversation id, which `agent-run-started` sends as is.
const runs = new Map();

/**
 * Bucket a duration, so the raw time never leaves the browser.
 * @param {number} ms
 */
export const bucketDuration = (ms) => {
  if (ms < 5000) return 'under_5s';
  if (ms < 15000) return '5s_to_15s';
  if (ms < 60000) return '15s_to_60s';
  return 'over_60s';
};

const finishRun = (threadId, run, segmentStart, { outcome, errorCode = null }) => {
  runs.delete(threadId);
  trackAgentRunCompleted({
    outcome,
    durationBucket: bucketDuration(run.activeMs + (Date.now() - segmentStart)),
    toolCount: run.toolCallIds.size,
    errorCode,
  });
};

/**
 * Pass `events` through unchanged while reporting the run they belong to.
 *
 * @param {string} url - The stream endpoint's path.
 * @param {AsyncIterable<{event: string, data: any}>} events
 * @param {Array<{key: string, value: unknown}>} [filters] - The request's `filters`, for `filter_applied`.
 * @yields {{event: string, data: any}} Every event, untouched.
 */
export async function* withAgentRunTelemetry(url, events, filters) {
  const match = ASSIST_URL.exec(url);
  if (!match) {
    yield* events;
    return;
  }

  const [, threadId, route] = match;
  const isResume = route === 'commands/resume';
  let run = isResume ? runs.get(threadId) : null;
  if (!run) {
    // `activeMs` excludes the time spent waiting on the user at an approval card.
    run = { activeMs: 0, toolCallIds: new Set() };
    runs.set(threadId, run);
    if (!isResume) {
      trackAgentRunStarted({
        conversationId: threadId,
        conversationType: CONVERSATION_TYPE,
        filterApplied: describeFilters(filters),
      });
    }
  }

  const segmentStart = Date.now();
  let paused = false;

  try {
    for await (const item of events) {
      if (item.event === 'tools' && item.data?.tool_call_id) {
        run.toolCallIds.add(item.data.tool_call_id);
      }
      if (item.event === 'interrupts' && Array.isArray(item.data) && item.data.length > 0) {
        paused = true;
      }
      yield item;
    }
  } catch (error) {
    finishRun(threadId, run, segmentStart, { outcome: 'error', errorCode: error?.status ?? null });
    throw error;
  }

  if (paused) {
    run.activeMs += Date.now() - segmentStart;
    return;
  }

  finishRun(threadId, run, segmentStart, { outcome: 'success' });
}
